import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, forkJoin, of, takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { SMEService } from '../../services/sme.service';
import { CompanyDetails } from '../../../../shared/models/company-details';
import { MatchedRecordConnections } from '../../../outreach/models/connection.model';
import { ExternalMatchesComponent } from '../external-matches/external-matches.component';

@Component({
  selector: 'app-matches',
  templateUrl: './matches.component.html',
  styleUrls: ['./matches.component.scss'],
  standalone: true,
  imports: [CommonModule, ExternalMatchesComponent],
})
export class MatchesComponent extends MenuComponentBase implements OnInit {
  private smeService = inject(SMEService);
  private router = inject(Router);

  activeTab: 'internal' | 'external' | 'applied' = 'internal';

  isLoadingInternal = false;
  hasInternalError = false;
  hasNoProfile = false;

  businessProfile: CompanyDetails | null = null;

  externalMatchesCount = 0;
  appliedLeadsCount = 0;
  isLoadingExternalCounts = true;

  get internalMatches(): MatchedRecordConnections[] {
    const list = this.businessProfile?.matched_record_connections;
    if (!list || !Array.isArray(list)) {
      return [];
    }
    return [...list]
      .map((conn) => ({
        ...conn,
        matched_criterias: Array.isArray(conn.matched_criterias)
          ? [...conn.matched_criterias].sort(
              (c1, c2) =>
                (c2.match_percentage || 0) - (c1.match_percentage || 0),
            )
          : [],
      }))
      .sort((a, b) => {
        const pctA = this.getBestMatchPercentage(a);
        const pctB = this.getBestMatchPercentage(b);
        if (pctB !== pctA) {
          return pctB - pctA; // Highest match percentage first
        }
        const progsA = a.matched_criterias?.length || 0;
        const progsB = b.matched_criterias?.length || 0;
        if (progsB !== progsA) {
          return progsB - progsA;
        }
        const nameA = (a.display_name || a.connection_name || '').toLowerCase();
        const nameB = (b.display_name || b.connection_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }

  get canShowExternalMatchesTab(): boolean {
    return this.externalMatchesCount > 0 || this.appliedLeadsCount > 0;
  }

  get canShowAppliedLeadsTab(): boolean {
    return this.appliedLeadsCount > 0;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const tab = params['tab'];
        if (tab === 'applied') {
          this.activeTab = 'applied';
        } else if (tab === 'external') {
          this.activeTab = 'external';
        } else {
          this.activeTab = 'internal';
        }
      });

    this.loadInternalMatches();
    this.loadExternalCounts();
  }

  loadInternalMatches(): void {
    this.isLoadingInternal = true;
    this.hasInternalError = false;
    this.hasNoProfile = false;

    this.smeService
      .getSMEBusinessRecord()
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          if (err?.status === 404) {
            this.hasNoProfile = true;
          } else {
            this.hasInternalError = true;
            console.error('Error loading matches:', err);
          }
          return of(null);
        }),
      )
      .subscribe((profile) => {
        this.isLoadingInternal = false;
        this.businessProfile = profile;
      });
  }

  loadExternalCounts(): void {
    this.isLoadingExternalCounts = true;
    forkJoin({
      crf: this.smeService.getCRFMatches().pipe(catchError(() => of(null))),
      applied: this.smeService
        .getAppliedCRFLeads()
        .pipe(catchError(() => of([]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ crf, applied }) => {
        this.isLoadingExternalCounts = false;
        this.externalMatchesCount = Array.isArray(crf?.matches)
          ? crf.matches.length
          : 0;
        this.appliedLeadsCount = Array.isArray(applied) ? applied.length : 0;

        // If currently on a tab that isn't available, fallback gracefully
        if (this.activeTab === 'applied' && !this.canShowAppliedLeadsTab) {
          this.setTab(this.canShowExternalMatchesTab ? 'external' : 'internal');
        } else if (
          this.activeTab === 'external' &&
          !this.canShowExternalMatchesTab
        ) {
          this.setTab('internal');
        }
      });
  }

  setTab(tab: 'internal' | 'external' | 'applied'): void {
    this.activeTab = tab;
    void this.router.navigate([], {
      queryParams: { tab: tab === 'internal' ? null : tab },
      queryParamsHandling: 'merge',
    });
  }

  onCountsChange(counts: { matches: number; applied: number }): void {
    this.externalMatchesCount = counts.matches;
    this.appliedLeadsCount = counts.applied;
  }

  onExternalTabChange(tab: 'matches' | 'applied'): void {
    this.setTab(tab === 'applied' ? 'applied' : 'external');
  }

  goBack(): void {
    this.navigateToBusinessProfile();
  }

  navigateToBusinessProfile(): void {
    void this.router.navigate(['/business']);
  }

  navigateToCdfiProfile(connectionName: string): void {
    if (!connectionName) return;
    void this.router.navigate(['/cdfi/profile', connectionName]);
  }

  getOrgInitial(name: string): string {
    return (name || 'O').charAt(0).toUpperCase();
  }

  getBestMatchPercentage(conn: MatchedRecordConnections): number {
    if (!conn.matched_criterias || conn.matched_criterias.length === 0) {
      return 0;
    }
    return Math.max(
      ...conn.matched_criterias.map((c) => c.match_percentage || 0),
    );
  }

  getMatchPercentageClass(pct: number): string {
    if (pct >= 75) return 'pct-high';
    if (pct >= 40) return 'pct-mid';
    return 'pct-low';
  }

  getAllProducts(conn: MatchedRecordConnections): string[] {
    if (!conn.matched_criterias) return [];
    const set = new Set<string>();
    for (const c of conn.matched_criterias) {
      if (c.matched_criteria?.products) {
        for (const p of c.matched_criteria.products) {
          if (p) {
            const cleaned = this.cleanProductName(p);
            if (cleaned) {
              set.add(cleaned);
            }
          }
        }
      }
    }
    return Array.from(set);
  }

  cleanProductName(name: string | undefined | null): string {
    if (!name || typeof name !== 'string') return '';
    const trimmed = name.trim();
    const lower = trimmed.toLowerCase();

    // Map common raw product keys to polished, professional display names
    const knownMap: Record<string, string> = {
      loans_capital: 'Loans & Capital',
      'loans-capital': 'Loans & Capital',
      working_capital: 'Working Capital',
      working_capital_loan: 'Working Capital Loan',
      working_capital_loans: 'Working Capital Loan',
      commercial_real_estate: 'Commercial Real Estate',
      real_estate: 'Real Estate',
      equipment_financing: 'Equipment Financing',
      equipment_loan: 'Equipment Loan',
      equipment_loans: 'Equipment Loan',
      technical_assistance: 'Technical Assistance',
      operational_tools: 'Operational Tools',
      finding_investors: 'Finding Investors',
      solar_energy: 'Solar & Energy',
      solar_loan: 'Solar Loan',
      line_of_credit: 'Line of Credit',
      lines_of_credit: 'Lines of Credit',
      micro_loan: 'Microloan',
      micro_loans: 'Microloan',
      microloan: 'Microloan',
      term_loan: 'Term Loan',
      term_loans: 'Term Loan',
      invoice_factoring: 'Invoice Factoring',
      sba_loan: 'SBA Loan',
      sba_7a: 'SBA 7(a) Loan',
      sba_504: 'SBA 504 Loan',
      grant: 'Grant',
      grants: 'Grants',
    };

    if (knownMap[lower]) {
      return knownMap[lower];
    }

    // Generic formatting: replace underscores/dashes/camelCase and convert to Title Case
    const spaced = trimmed
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return spaced
      .split(' ')
      .map((word) => {
        const wLower = word.toLowerCase();
        if (
          wLower === 'sba' ||
          wLower === 'cdfi' ||
          wLower === 'cre' ||
          wLower === 'loc'
        ) {
          return word.toUpperCase();
        }
        if (wLower === 'and' || wLower === '&') return '&';
        if (wLower === 'of') return 'of';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  getAllProperties(conn: MatchedRecordConnections): string[] {
    if (!conn.matched_criterias) return [];
    const set = new Set<string>();
    for (const c of conn.matched_criterias) {
      if (c.matched_criteria?.matched_properties) {
        for (const p of c.matched_criteria.matched_properties) {
          if (p) set.add(this.formatPropertyName(p));
        }
      }
    }
    return Array.from(set);
  }

  formatPropertyName(prop: string): string {
    if (!prop) return '';
    return prop
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
}
